import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { formatDateCs, MEAL_TYPE_LABELS } from "../lib/utils";
import type { MealPlan, Recipe, Ingredient } from "../types";

export default function DashboardPage() {
  const plansQ = useQuery({
    queryKey: ["meal-plans"],
    queryFn: () => api.get<{ mealPlans: MealPlan[] }>("/api/meal-plans"),
  });
  const recipesQ = useQuery({
    queryKey: ["recipes"],
    queryFn: () => api.get<{ recipes: Recipe[] }>("/api/recipes"),
  });
  const ingredientsQ = useQuery({
    queryKey: ["ingredients"],
    queryFn: () => api.get<{ ingredients: Ingredient[] }>("/api/ingredients"),
  });

  const todayIso = new Date().toISOString().slice(0, 10);
  const active = (plansQ.data?.mealPlans ?? []).find((p) => p.dateFrom <= todayIso && p.dateTo >= todayIso);
  const upcoming = (plansQ.data?.mealPlans ?? []).filter((p) => p.dateFrom > todayIso).slice(0, 3);

  return (
    <div>
      <PageHeader title="Vítejte zpět!" description="Rychlý přehled vašeho rodinného jídelníčku." />

      <div className="grid gap-4 md:grid-cols-3 mb-6">
        <Stat icon="🍳" label="Receptů" value={recipesQ.data?.recipes.length ?? 0} to="/recepty" />
        <Stat icon="🥕" label="Surovin v katalogu" value={ingredientsQ.data?.ingredients.length ?? 0} to="/suroviny" />
        <Stat icon="📅" label="Jídelníčků" value={plansQ.data?.mealPlans.length ?? 0} to="/jidelnicky" />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="card card-body">
          <h2 className="font-semibold mb-3 flex items-center gap-2">
            <span aria-hidden>📌</span>
            <span>Aktivní jídelníček</span>
          </h2>
          {active ? (
            <div>
              <Link to={`/jidelnicky/${active.id}`} className="font-medium text-brand-700 hover:underline">
                {active.name}
              </Link>
              <p className="text-xs text-slate-500 mt-1">
                {formatDateCs(active.dateFrom)} – {formatDateCs(active.dateTo)}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                {(active.mealTypes ?? []).map((m) => MEAL_TYPE_LABELS[m] ?? m).join(", ")}
              </p>
            </div>
          ) : (
            <p className="text-sm text-slate-500">
              Žádný aktivní jídelníček. <Link className="underline" to="/jidelnicky/novy">Vytvořit</Link>
            </p>
          )}
        </div>

        <div className="card card-body">
          <h2 className="font-semibold mb-3 flex items-center gap-2">
            <span aria-hidden>🔮</span>
            <span>Nadcházející</span>
          </h2>
          {upcoming.length === 0 ? (
            <p className="text-sm text-slate-500">Zatím nic plánovaného.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {upcoming.map((p) => (
                <li key={p.id}>
                  <Link to={`/jidelnicky/${p.id}`} className="text-brand-700 hover:underline">
                    {p.name}
                  </Link>{" "}
                  <span className="text-slate-500">
                    ({formatDateCs(p.dateFrom)} – {formatDateCs(p.dateTo)})
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, to, icon }: { label: string; value: number; to: string; icon: string }) {
  return (
    <Link to={to} className="card card-body hover:bg-brand-50/40 transition-colors">
      <div className="flex items-center justify-between">
        <div className="text-3xl font-bold text-brand-700">{value}</div>
        <div aria-hidden className="text-3xl opacity-70">
          {icon}
        </div>
      </div>
      <div className="text-sm text-slate-500 mt-1">{label}</div>
    </Link>
  );
}
